import { useParams } from "react-router-dom";
import { usePedido } from "../../hooks/pagamento/usePedido";
import { Loading } from "../../components/Loading";
import { Box, Flex, useDisclosure } from "@chakra-ui/react";
import { Pedido } from "./Pedido";
import { Header } from "../../components/Header";
import { Bilhetes } from "./Bilhetes";
import { Actions } from "./Actions";
import Swal from "sweetalert2";
import { useMutationCancelar } from "../../hooks/pagamento/mutationCancelar";
import { Footer } from "../../components/Footer";
import { Bilhete, PedidoStatusCode } from "../../utils/interfaces";
import { useMutationMail } from "../../hooks/pagamento/mutationMail";
import { getUser, isEmissor, isRepresentante } from "../../libs/auth";
import { deveBloquearDevolucaoNoPedido, deveBloquearDevolucaoPresencialNoPedido } from "../../utils/devolucao";
import { ModalConfirmarDevolucao } from "./ModalConfirmarDevolucao";
import { isMultaNaFatura } from "../../utils/multaCancelamento";

function listarBilhetes(bilhetes: Record<string, unknown>): Bilhete[] {
  return Object.values(bilhetes || {}).flatMap((grupos) =>
    ((grupos as Array<{ viagens?: Array<{ bilhetes?: Bilhete[] }> }>) || []).flatMap((grupo) =>
      (grupo.viagens || []).flatMap((viagem) => viagem.bilhetes || [])
    )
  );
}

function totalMulta(bilhetes: Record<string, unknown>): number {
  return listarBilhetes(bilhetes).reduce((total, bilhete) => {
    const multa = Number(bilhete.multa ?? bilhete.multa_cancelamento ?? 0);

    return total + (Number.isFinite(multa) ? multa : 0);
  }, 0);
}

function multaPorBilhete(bilhetes: Record<string, unknown>): number {
  const bilheteComMulta = listarBilhetes(bilhetes).find((bilhete) => {
    const multa = Number(bilhete.multa ?? bilhete.multa_cancelamento ?? 0);

    return Number.isFinite(multa) && multa > 0;
  });

  return Number(bilheteComMulta?.multa ?? bilheteComMulta?.multa_cancelamento ?? 0);
}

function possuiBilheteGpApi(bilhetes: Record<string, unknown>): boolean {
  return listarBilhetes(bilhetes).some(
    (bilhete) => String(bilhete.adaptador ?? bilhete.adapter ?? "").toUpperCase() === "GP-API"
  );
}

export function DetalhePedido(){
  const { id } = useParams() as { id: string };
  const { data, isLoading } = usePedido({ id }); 
  const user = getUser();
  const representanteOnly = isRepresentante();
  const emissorOnly = isEmissor();

  const mutation = useMutationCancelar()
  const mutationmail = useMutationMail();
  const modalCancelamento = useDisclosure();

  if (isLoading) return <Loading />;

  if (typeof data === "undefined") return;

  const isDevolucao = [
    PedidoStatusCode.Finalizado,
    PedidoStatusCode.DevolvidoParcialmente,
  ].includes(data.pedido.status);
  const userAgenciaId = user?.agencia?.id;
  const pedidoAgenciaId = data.pedido.agencia_id;
  const userId = user?.id;
  const pedidoUsuarioId = data.pedido.usuario_id;
  const representantePodeCancelar = !representanteOnly
    || (pedidoAgenciaId !== undefined
      && userAgenciaId !== undefined
      && String(pedidoAgenciaId) === String(userAgenciaId));
  const emissorPodeCancelar = !emissorOnly
    || (pedidoUsuarioId !== undefined
      && userId !== undefined
      && String(pedidoUsuarioId) === String(userId));
  const canCancelPedido = representantePodeCancelar && emissorPodeCancelar;
  const bloqueioDevolucaoAtivo = deveBloquearDevolucaoNoPedido(data.bilhetes);
  const bloqueioDevolucaoPresencial = data.pedido.devolucao_presencial
    || deveBloquearDevolucaoPresencialNoPedido(data.bilhetes);
  const exibirBotaoDevolucaoPedido = canCancelPedido
    && (!isDevolucao || (!bloqueioDevolucaoAtivo && !bloqueioDevolucaoPresencial));
  const agenciaRepassaMulta = Boolean(data.pedido.repassar_multa ?? user?.agencia?.repassar_multa);
  const multaPedido = totalMulta(data.bilhetes);
  const multaPedidoPorBilhete = multaPorBilhete(data.bilhetes);
  const pedidoTemGpApi = possuiBilheteGpApi(data.bilhetes);
  const multaNaFatura = isDevolucao
    && multaPedido > 0
    && pedidoTemGpApi
    && isMultaNaFatura(data.pedido, Boolean(user?.agencia?.repassar_multa));

  const handleCancel = () => {
    modalCancelamento.onOpen();
  }

  const confirmarCancelamento = async () => {
    await mutation.mutateAsync(data.pedido.id);
    modalCancelamento.onClose();
  }

  const handleMail = async () => {
    Swal.fire({
      title: "Enviar email?",
      text: "O comprovante será enviado para o email cadastrado!",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim",
      cancelButtonText: "Não",
    }).then(async (response) => {
      if(response.isConfirmed){
        await mutationmail.mutateAsync({id: data.pedido.id})
      }
    })
  }

  return (
    <Box
        display={"flex"}
        flexDir={"column"}
        justifyContent={"center"}
        width={"full"}
        alignItems={"center"}
      >
        {mutation.isPending && <Loading />}
        <Flex
          maxWidth={"90%"}
          width={"100%"}
          flexDir={"column"}
          justifyContent={"center"}
        >
          <Box
            px={"1rem"}
            bg={"white"}
            w={"full"}
            display={"flex"}
            flexDir={"column"}
            gap={"8"}
          >
            <Header />
            <Actions
              onEmail={handleMail}
              onCancel={handleCancel}
              isCancel={[
                PedidoStatusCode.Cancelado,
                PedidoStatusCode.EstornoPendente,
                PedidoStatusCode.Devolvido,
                PedidoStatusCode.Contestado,
              ].includes(data.pedido.status)}
              isDevolucao={isDevolucao}
              showCancel={exibirBotaoDevolucaoPedido}
            />
            <Flex gap={2} width={"100%"} minW={"400px"}>
              <Pedido
                pedido={data.pedido}
                totalTaxaCancelamentoRetida={agenciaRepassaMulta ? multaPedido : 0}
              />
            </Flex>
            <Bilhetes
              bilhetes={data.bilhetes}
              showCancel={canCancelPedido}
              agenciaRepassaMulta={agenciaRepassaMulta}
              multaNaFatura={multaNaFatura}
            />
          </Box>
        <Footer />
        </Flex>
        <ModalConfirmarDevolucao
          isOpen={modalCancelamento.isOpen}
          onClose={modalCancelamento.onClose}
          onConfirm={confirmarCancelamento}
          isLoading={mutation.isPending}
          titulo={`Tem certeza que deseja ${isDevolucao ? 'devolver' : 'cancelar'} este pedido?`}
          descricao={isDevolucao && agenciaRepassaMulta && pedidoTemGpApi
            ? [
              "Esta devolução tem uma taxa de cancelamento, definido pela viação.",
              "Após a devolução verifique a conta corrente para confirmar o valor.",
            ]
            : isDevolucao
              ? "Deseja continuar com a devolução deste pedido?"
              : "O cancelamento será solicitado para este pedido."}
          confirmLabel={isDevolucao ? "Devolver pedido" : "Cancelar pedido"}
          multa={multaPedidoPorBilhete}
          exibirMulta={isDevolucao && agenciaRepassaMulta && pedidoTemGpApi}
          multaNaFatura={multaNaFatura}
        />
      </Box>
  )
}
